import { NavLink } from 'react-router-dom';
import { BookOpen, Building2, FileSearch, Home, Settings2, Users } from 'lucide-react';
import type { Membership, Tenant } from '../api/types.js';
import { VersionTag } from '../components/VersionTag.js';
import { initials } from '../lib/initials.js';

interface SidebarProps {
  tenants: Tenant[];
  activeTenantId: string | null;
  callerMemberships: Membership[];
  callerDisplayName: string;
  callerEmail: string | null;
  isAdmin: boolean;
  validatorOnly?: boolean;
  // REQ-FE-034 area B — keeps the existing live behaviour of routing
  // Annuaire to a per-tenant page (vs the mockup's drawer). Caller
  // resolves the URL since active tenant + first-available logic
  // lives in AppShell.
  annuaireHref: string | null;
}

// REQ-FE-034 area B — Sidebar ported verbatim from the mockup source
// at `.claude/worktrees/feat+web-vite-react-scaffold/apps/web/src/components/Sidebar.tsx`.
// Tailwind utility classes copied character-for-character. Adapted
// only: router imports (react-router-dom instead of @tanstack/react-router),
// type imports (real `Tenant` + `Membership`), data sources (passed in
// from AppShell instead of mock-data lookups).
export function Sidebar({
  tenants,
  activeTenantId,
  callerMemberships,
  callerDisplayName,
  callerEmail,
  isAdmin,
  validatorOnly = false,
  annuaireHref,
}: SidebarProps) {
  const ops = tenants.filter((t) => !isSciTenant(t));
  const scis = tenants.filter(isSciTenant);

  return (
    <aside
      className="flex w-60 shrink-0 flex-col px-4 py-5"
      style={{ borderRight: '1px solid var(--gray-200)', background: 'var(--cream-50)' }}
      data-testid="shell-sidebar"
    >
      <div className="flex flex-1 flex-col gap-6 overflow-auto">
        <nav className="flex flex-col gap-1">
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `flex items-center gap-2 px-2 py-1.5 text-[13px]${isActive ? ' is-active' : ''}`
            }
            style={({ isActive }) =>
              isActive
                ? { color: 'var(--ink)', background: 'var(--cream-100)', fontWeight: 600 }
                : { color: 'var(--gray-700)' }
            }
            data-testid="nav-dashboard"
          >
            <Home size={14} strokeWidth={1.5} />
            Vue d&apos;ensemble
          </NavLink>
        </nav>

        {!validatorOnly && ops.length > 0 ? (
          <section className="flex flex-col gap-2">
            <p
              className="px-2 text-[9px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: 'var(--gray-500)' }}
            >
              Sociétés opérationnelles
            </p>
            <ul className="flex flex-col gap-0.5">
              {ops.map((t) => (
                <TenantLink key={t.tenantId} tenant={t} active={t.tenantId === activeTenantId} />
              ))}
            </ul>
          </section>
        ) : null}

        {!validatorOnly && scis.length > 0 ? (
          <section className="flex flex-col gap-2">
            <p
              className="px-2 text-[9px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: 'var(--gray-500)' }}
              data-testid="sci-section-label"
            >
              Immobilier (SCI)
            </p>
            <ul className="flex flex-col gap-0.5">
              {scis.map((t) => (
                <TenantLink key={t.tenantId} tenant={t} active={t.tenantId === activeTenantId} />
              ))}
            </ul>
          </section>
        ) : null}

        {!validatorOnly && annuaireHref ? (
          <section className="flex flex-col gap-2">
            <p
              className="px-2 text-[9px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: 'var(--gray-500)' }}
            >
              Outils
            </p>
            <NavLink
              to={annuaireHref}
              className="flex items-center gap-2 px-2 py-1.5 text-left text-[13px]"
              style={{ color: 'var(--gray-700)' }}
              title="Annuaire fournisseurs (⇧A)"
              data-testid="nav-outils-annuaire"
            >
              <BookOpen size={14} strokeWidth={1.5} />
              Annuaire fournisseurs
              <span
                className="ml-auto text-[9px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--gray-500)' }}
                aria-hidden="true"
              >
                ⇧A
              </span>
            </NavLink>
          </section>
        ) : null}
      </div>

      {/* Bottom admin block — role-gated. Identity row carries the
          ADMIN chip when caller is group_admin (FE-032 D19/D20). */}
      <section
        className="mt-4 flex flex-col gap-2 pt-4"
        style={{ borderTop: '1px solid var(--gray-200)' }}
      >
        {isAdmin && !validatorOnly ? (
          <>
            <p
              className="px-2 text-[9px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: 'var(--gray-500)' }}
            >
              Administration
            </p>
            <ul className="flex flex-col gap-0.5">
              <AdminLink
                to="/admin/members"
                icon={<Users size={14} strokeWidth={1.5} />}
                label="Membres"
                testId="nav-admin-members"
              />
              <AdminLink
                to="/admin/audit"
                icon={<FileSearch size={14} strokeWidth={1.5} />}
                label="Audit"
                testId="nav-admin-audit"
              />
              <AdminLink
                to="/admin/settings"
                icon={<Settings2 size={14} strokeWidth={1.5} />}
                label="Paramètres"
                testId="nav-admin-settings"
              />
            </ul>
          </>
        ) : null}

        <div
          className="mt-2 flex items-center gap-2 px-2 py-1.5 text-[11px]"
          style={{ color: 'var(--gray-600)' }}
          title={callerEmail ?? ''}
          data-testid="sidebar-user-row"
        >
          <span
            className="inline-flex h-5 w-5 items-center justify-center text-[10px] font-semibold"
            style={{
              background: 'var(--ink)',
              color: 'var(--white)',
              borderRadius: '999px',
            }}
            aria-hidden="true"
          >
            {initials(callerDisplayName)}
          </span>
          <span className="truncate">{callerDisplayName || callerEmail || '—'}</span>
          {isAdmin ? (
            <span
              className="ml-auto text-[9px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--brand-coral-dark)' }}
              data-testid="sidebar-admin-chip"
            >
              Admin
            </span>
          ) : null}
        </div>
        {/* REQ-FE-035 item 3 (re-shipped in REQ-FE-036 bundle): build
            SHA chip below identity row. Orch + user use this for
            deploy verification (memory `feedback_deploy_sha_check`). */}
        <div className="mt-1 px-2">
          <VersionTag />
        </div>
      </section>

      {/* Suppress unused-var warning while we wait for callerMemberships
          to drive a richer identity panel (role pill etc.). */}
      <span hidden aria-hidden="true">
        {callerMemberships.length}
      </span>
    </aside>
  );
}

interface TenantLinkProps {
  tenant: Tenant;
  active: boolean;
}

function TenantLink({ tenant, active }: TenantLinkProps) {
  return (
    <li>
      <NavLink
        to={`/t/${tenant.tenantId}`}
        end
        className={`flex items-center gap-2 px-2 py-1.5 text-[13px]${active ? ' is-active' : ''}`}
        style={{
          color: 'var(--gray-700)',
          background: active ? 'var(--cream-100)' : 'transparent',
          fontWeight: active ? 600 : 400,
          borderLeft: active ? '3px solid var(--brand-coral)' : '3px solid transparent',
        }}
        data-testid={`tenant-link-${tenant.tenantId}`}
        aria-current={active ? 'page' : undefined}
      >
        <span
          aria-hidden="true"
          className="inline-block h-1.5 w-1.5"
          style={{ background: 'var(--brand-coral)', borderRadius: '999px' }}
        />
        <Building2 size={14} strokeWidth={1.5} style={{ opacity: 0.7 }} />
        <span className="truncate" title={tenant.legalName}>
          {tenant.legalName}
        </span>
      </NavLink>
    </li>
  );
}

interface AdminLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  testId: string;
}

function AdminLink({ to, icon, label, testId }: AdminLinkProps) {
  return (
    <li>
      <NavLink
        to={to}
        className="flex items-center gap-2 px-2 py-1.5 text-[13px]"
        style={({ isActive }) =>
          isActive
            ? {
                color: 'var(--ink)',
                background: 'var(--cream-100)',
                fontWeight: 600,
                borderLeft: '3px solid var(--brand-coral)',
              }
            : {
                color: 'var(--gray-700)',
                borderLeft: '3px solid transparent',
              }
        }
        data-testid={testId}
      >
        {icon}
        <span className="truncate">{label}</span>
      </NavLink>
    </li>
  );
}

function isSciTenant(t: Tenant): boolean {
  return /^SCI\s/i.test(t.legalName);
}
